import type { ItemKey, SpaceKey } from '@toolproof-core/visualization/_contract/key';
import type {
    InterpolatedFramePatch,
    SpaceDerivationFrame,
    TransitionPhase,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toNaturalComparisonOrbitConstellationKey, toPrimitiveHubConstellationKey, toPrimitivePowerConstellationKey, toPrimitiveWheelTitleItemKey } from '@/adapters/primitive/_pure/derivation/keyCodec';
import {
    buildPrimitiveRodFlightPatch,
    buildPrimitiveRodFrame,
    toScopedPrimitiveRodKey,
    type PrimitiveRodPlacement,
    type PrimitiveRodScope,
} from '@/adapters/primitive/_pure/derivation/primitiveRod';
import type { BooleanRodState } from '@/adapters/primitive/_contract/boolean/rod';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

/** Undo the actual traversed edges, including helper creation and removal keyframes. */
export function reversePrimitiveStepStates(
    steps: readonly PrimitiveStepState[], fromIndex: number, toIndex: number,
): PrimitiveStepState {
    const destination = steps[toIndex];
    const keyframes: PrimitiveStepState['keyframes'][number][] = [];
    for (let index = fromIndex; index > toIndex; index--) {
        const forward = steps[index].keyframes;
        for (let frameIndex = forward.length - 1; frameIndex >= 0; frameIndex--) {
            const previous = frameIndex === 0 ? steps[index - 1].keyframes.at(-1)! : forward[frameIndex - 1];
            const transition = forward[frameIndex].transition;
            keyframes.push({ ...previous, events: [], transition: transition !== 'instant' && transition.comparisonEyeOrbitAngle
                ? { ...transition, comparisonEyeOrbitAngle: -transition.comparisonEyeOrbitAngle } : transition });
        }
    }
    const reversed: PrimitiveStepState = { ...destination, stepKey: `reverse-${steps[fromIndex].stepKey}-to-${destination.stepKey}`, keyframes };
    // A reversal is a synthetic traversal, not the destination's moment.
    delete reversed.moment;
    return reversed;
}

export function toPrimitiveGeometryFramePatch(frame: SpaceDerivationFrame): SpaceDerivationFrame {
    if (!frame.spaceRenderOptions) return frame;
    // Base-frame camera defaults are not navigation requests for geometry keyframes.
    const spaceRenderOptions = { ...frame.spaceRenderOptions };
    delete spaceRenderOptions.camera;
    return { ...frame, spaceRenderOptions };
}

export function derivePrimitiveStepTransitionPhases(args: {
    spaceKey: SpaceKey;
    config: Config;
    previousWheels: readonly PrimitiveWheelState[];
    previousBooleanRod?: BooleanRodState;
    step: PrimitiveStepState;
    scope?: PrimitiveRodScope;
    placement?: PrimitiveRodPlacement;
    postPlacementOverride?: (wheel: PrimitiveWheelState) => PrimitiveWheelState;
    transitionKeyPrefix?: string;
    /** Wraps geometry keyframes; the optional rodFrame avoids re-derivation. Camera declarations are excluded. */
    buildInstantFrame?: (wheels: readonly PrimitiveWheelState[], booleanRod?: BooleanRodState, rodFrame?: SpaceDerivationFrame) => SpaceDerivationFrame;
}): TransitionPhase[] {
    const buildFrame = args.buildInstantFrame ?? ((wheels: readonly PrimitiveWheelState[], booleanRod?: BooleanRodState) => buildPrimitiveRodFrame({
        spaceKey: args.spaceKey,
        config: args.config,
        wheels,
        ...(booleanRod === undefined ? {} : { booleanRod }),
        scope: args.scope,
        placement: args.placement,
        postPlacementOverride: args.postPlacementOverride,
    }));
    const buildInstantFrame = (wheels: readonly PrimitiveWheelState[], booleanRod?: BooleanRodState, rodFrame?: SpaceDerivationFrame) => {
        return toPrimitiveGeometryFramePatch(buildFrame(wheels, booleanRod, rodFrame));
    };
    const transitionKeyPrefix = args.transitionKeyPrefix ?? args.step.stepKey;
    const phases: TransitionPhase[] = [];
    let previousWheels = args.previousWheels;
    let previousBooleanRod = args.previousBooleanRod;
    const buildRodFrame = (wheels: readonly PrimitiveWheelState[], booleanRod?: BooleanRodState) => buildPrimitiveRodFrame({
        spaceKey: args.spaceKey, config: args.config, wheels,
        ...(booleanRod === undefined ? {} : { booleanRod }),
        scope: args.scope, placement: args.placement, postPlacementOverride: args.postPlacementOverride,
    });
    let previousFrame: SpaceDerivationFrame | undefined;

    for (const [keyframeIndex, keyframe] of args.step.keyframes.entries()) {
        const transitionKey = `${transitionKeyPrefix}:${keyframeIndex}`;
        if (keyframe.transition === 'instant') {
            previousFrame = buildRodFrame(keyframe.wheels, keyframe.booleanRod);
            phases.push({
                patch: args.buildInstantFrame === undefined
                    ? toPrimitiveGeometryFramePatch(previousFrame)
                    : buildInstantFrame(keyframe.wheels, keyframe.booleanRod, previousFrame),
                transitionOptions: {
                    transitionKind: 'instant',
                    transitionKey,
                    delay: args.config.base.animation.instantDelayMs,
                },
            });
        } else {
            previousFrame ??= buildRodFrame(previousWheels, previousBooleanRod);
            const nextFrame = buildRodFrame(keyframe.wheels, keyframe.booleanRod);
            let patch = buildPrimitiveRodFlightPatch({
                spaceKey: args.spaceKey,
                config: args.config,
                previousWheels,
                nextWheels: keyframe.wheels,
                ...(previousBooleanRod === undefined ? {} : { previousBooleanRod }),
                ...(keyframe.booleanRod === undefined ? {} : { nextBooleanRod: keyframe.booleanRod }),
                scope: args.scope,
                placement: args.placement,
                postPlacementOverride: args.postPlacementOverride,
                previousFrame,
                nextFrame,
            });
            if (keyframe.transition.comparisonEyeOrbitAngle !== undefined && keyframe.transition.comparisonEyeOrbitAngle !== 0) {
                const guideKey = toScopedPrimitiveRodKey(toNaturalComparisonOrbitConstellationKey(), args.scope);
                const guideAnchor = previousFrame.constellationParametricState?.entityConstellationByKey?.[guideKey]?.layout?.anchor;
                if (guideAnchor?.anchorKind !== 'position') throw new Error('Eye orbit requires an existing guide center.');
                const axis: [number, number, number] = args.placement === undefined
                    ? [0, 0, 1]
                    : [args.placement.axis[0], args.placement.axis[1], args.placement.axis[2]];
                const orbitByKey: NonNullable<InterpolatedFramePatch['entityConstellationOrbitByKey']> = {};
                for (const key of [
                    toPrimitivePowerConstellationKey({ wheelRole: 'less-than-decision', power: 0 }),
                    toPrimitiveHubConstellationKey({ wheelRole: 'less-than-decision' }),
                ]) {
                    orbitByKey[toScopedPrimitiveRodKey(key, args.scope)] = {
                        center: guideAnchor.position, axis, angle: keyframe.transition.comparisonEyeOrbitAngle,
                    };
                }
                patch = { ...patch, entityConstellationOrbitByKey: orbitByKey };
            }
            const hiddenTitles: Partial<Record<ItemKey<'entity'>, { visualStyle: { opacity: number } }>> = {};
            const revealedTitles: typeof hiddenTitles = {};
            const enteringHelpers = [...previousWheels, ...keyframe.wheels].filter(wheel =>
                (wheel.helperWheel?.kind === 'Assembly' || wheel.helperWheel?.kind === 'Unit')
                && (wheel.entranceGrowth ?? 1) < 1);
            const titleTransitionKind = enteringHelpers.some(wheel => wheel.helperWheel?.kind === 'Assembly')
                ? 'assembly' : 'unit';
            for (const wheel of enteringHelpers) {
                const key = toScopedPrimitiveRodKey(toPrimitiveWheelTitleItemKey({
                    wheelRole: wheel.role, helperKey: wheel.helperWheel!.key,
                }), args.scope);
                if (!previousFrame.itemParametricState?.entityItemByKey?.[key]) continue;
                hiddenTitles[key] = { visualStyle: { opacity: 0 } };
                const opacity = nextFrame.itemParametricState?.entityItemByKey?.[key]?.visualStyle?.opacity;
                if (opacity !== undefined && opacity > 0) revealedTitles[key] = { visualStyle: { opacity } };
            }
            if (patch && Object.keys(hiddenTitles).length) {
                // Flags appear only once their helper wheels are fully grown.
                phases.push({ patch: { itemParametricState: { entityItemByKey: hiddenTitles } },
                    transitionOptions: { transitionKind: 'instant', transitionKey: `${transitionKey}:${titleTransitionKind}-title-hide` } });
                patch = { ...patch, itemParametricState: { ...patch.itemParametricState,
                    entityItemByKey: { ...patch.itemParametricState?.entityItemByKey, ...hiddenTitles } } };
            }
            phases.push(patch === null
                ? {
                    patch: args.buildInstantFrame === undefined
                        ? toPrimitiveGeometryFramePatch(nextFrame)
                        : buildInstantFrame(keyframe.wheels, keyframe.booleanRod, nextFrame),
                    transitionOptions: {
                        transitionKind: 'instant',
                        transitionKey,
                        delay: keyframe.transition.durationMs,
                    },
                }
                : {
                    patch,
                    transitionOptions: {
                        transitionKind: 'interpolated',
                        transitionKey,
                        duration: keyframe.transition.durationMs,
                    },
                });
            if (patch && Object.keys(revealedTitles).length) {
                phases.push({ patch: { itemParametricState: { entityItemByKey: revealedTitles } },
                    transitionOptions: { transitionKind: 'instant', transitionKey: `${transitionKey}:${titleTransitionKind}-title-reveal` } });
            }
            previousFrame = nextFrame;
        }
        previousWheels = keyframe.wheels;
        previousBooleanRod = keyframe.booleanRod;
    }

    return phases;
}